/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/lookup_property_statuses-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-lookup_property_statuses-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/lookup_property_statuses-entity-config.ts
 * - Run: pnpm run generate:api-clients (regenerates this file)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id
 * - Sortable fields: created_at, updated_at
 * - Required fields: id, sort_order, archived, updated_at
 * - Readonly fields: id, created_at, updated_at
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/lookup_property_statuses-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-lookup_property_statuses-api-client.ts (API client)
 * - client/api/generated_schemas/lookup_property_statuses.ts (TypeBox schemas and types)
 *
 * Source config: config/entities/lookup_property_statuses-entity-config.ts
 * Public fields: 8/8 total fields
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LookupPropertyStatusesClient, LookupPropertyStatusesQueryOptions } from '../generated_clients/generated-lookup_property_statuses-api-client.js';
import {
  LookupPropertyStatusesInsert,
  LookupPropertyStatusesUpdate,
  LookupPropertyStatusesFieldConfig
} from '../../types/generated_schemas/generated-lookup_property_statuses-schema.js';

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/lookup_property_statuses_business_hooks/

// Query keys with enhanced caching strategy. A list key carries every
// parameter getAll sends, so two different pages never share a cache entry.
export const lookupPropertyStatusesKeys = {
  all: ['lookup_property_statuses'] as const,
  lists: () => [...lookupPropertyStatusesKeys.all, 'list'] as const,
  list: (filters: Record<string, any>, page?: Pick<LookupPropertyStatusesQueryOptions, 'limit' | 'offset' | 'sortBy' | 'sortOrder'>) =>
    [...lookupPropertyStatusesKeys.lists(), { filters, ...page }] as const,
  details: () => [...lookupPropertyStatusesKeys.all, 'detail'] as const,
  detail: (id: string) => [...lookupPropertyStatusesKeys.details(), id] as const,
  search: (query: string, fields?: string[]) => [...lookupPropertyStatusesKeys.all, 'search', query, fields] as const,
  filter: (filters: Record<string, any>) => [...lookupPropertyStatusesKeys.all, 'filter', filters] as const,
  sort: (sortBy: string, ascending: boolean) => [...lookupPropertyStatusesKeys.all, 'sort', sortBy, ascending] as const,
  count: (filters?: Record<string, any>) => [...lookupPropertyStatusesKeys.all, 'count', filters] as const,
};

// Client instance hook - uses the shared httpClient
export const useLookupPropertyStatusesClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new LookupPropertyStatusesClient();
};

/**
 * Hook to get all lookup_property_statuses with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with lookup_property_statuses data
 */
export const useLookupPropertyStatusesList = (options?: LookupPropertyStatusesQueryOptions) => {
  const client = useLookupPropertyStatusesClient();
  
  return useQuery({
    queryKey: lookupPropertyStatusesKeys.list(options?.filters || {}, {
      limit: options?.limit,
      offset: options?.offset,
      sortBy: options?.sortBy,
      sortOrder: options?.sortOrder,
    }),
    queryFn: () => client.getAll(options),
  });
};

/**
 * Hook to get a single lookup_property_statuse by ID
 *
 * @param id - The lookup_property_statuse ID
 * @returns React Query result with single lookup_property_statuse data
 */
export const useLookupPropertyStatuses = (id: string) => {
  const client = useLookupPropertyStatusesClient();
  
  return useQuery({
    queryKey: lookupPropertyStatusesKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/lookup_property_statuses
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/lookup_property_statuses_business_hooks/
 */

/**
 * Hook to create a new lookup_property_statuse
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: id, sort_order, archived, updated_at
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, created_at, updated_at
 *
 * @returns React Query mutation for creating lookup_property_statuse
 */
export const useCreateLookupPropertyStatuses = () => {
  const client = useLookupPropertyStatusesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: LookupPropertyStatusesInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lookupPropertyStatusesKeys.all });
    },
  });
};

/**
 * Hook to update an existing lookup_property_statuse
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, created_at, updated_at
 *
 * @returns React Query mutation for updating lookup_property_statuse
 */
export const useUpdateLookupPropertyStatuses = () => {
  const client = useLookupPropertyStatusesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: LookupPropertyStatusesUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: lookupPropertyStatusesKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: lookupPropertyStatusesKeys.lists() });
    },
  });
};

/**
 * Hook to delete a lookup_property_statuse
 *
 * @returns React Query mutation for deleting lookup_property_statuse
 */
export const useDeleteLookupPropertyStatuses = () => {
  const client = useLookupPropertyStatusesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lookupPropertyStatusesKeys.all });
    },
  });
};

/**
 * Hook to count lookup_property_statuses with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const useLookupPropertyStatusesCount = (filter?: Partial<Record<typeof LookupPropertyStatusesFieldConfig.filterable[number], any>>) => {
  const client = useLookupPropertyStatusesClient();
  
  return useQuery({
    queryKey: lookupPropertyStatusesKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const useLookupPropertyStatusesFieldConfig = () => {
  return LookupPropertyStatusesFieldConfig;
};